import React, { useState } from 'react'
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    CircularProgress,
    IconButton,
    SelectChangeEvent,
} from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import EditIcon from '@mui/icons-material/Edit'
import AddRoundedIcon from '@mui/icons-material/AddRounded' // ✅ Import Add icon
import { useTranslation } from 'react-i18next'

export interface IGenericDropdownOptions {
    id: string
    name: string
    canEdit?: boolean
}

interface Props {
    label: string
    name: string
    value: string
    options: IGenericDropdownOptions[]
    isLoading: boolean
    onChange: (event: SelectChangeEvent<string>) => void
    isMandatory?: boolean
    hasError?: boolean
    onClickEditRow?: (item: { id: string; name: string }) => void
    canAdd?: boolean
    onClickAddNew?: () => void
    hideLabel?: boolean
    id?: string // <-- add id prop
    selectElementWidth?: string
    marginBottom?: string
    disabled?: boolean
}

const GenericDropdown: React.FC<Props> = ({
    label,
    name,
    value,
    options,
    isLoading,
    onChange,
    isMandatory,
    hasError,
    onClickEditRow,
    canAdd,
    onClickAddNew,
    hideLabel,
    id,
    selectElementWidth = '300px',
    marginBottom,
    disabled,
}) => {
    const { t } = useTranslation()
    const [isDropDownOpen, setIsDropDownOpen] = useState(false)
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                mb: marginBottom || 2,
            }}
        >
            {!hideLabel && (
                <Typography sx={{ marginRight: '8px', width: '200px' }}>
                    {label}
                    {isMandatory && '*'}
                </Typography>
            )}
            <Box sx={{ width: selectElementWidth }}>
                <FormControl fullWidth error={hasError}>
                    <InputLabel id={`${id}-label`} htmlFor={id}>
                        {label}
                        {isMandatory && '*'}
                    </InputLabel>
                    <Select
                        id={id}
                        labelId={`${id}-label`}
                        title={label}
                        label={label}
                        name={name}
                        value={value}
                        onChange={onChange}
                        defaultValue={value}
                        open={isDropDownOpen}
                        onOpen={() => setIsDropDownOpen(true)}
                        onClose={() => setIsDropDownOpen(false)}
                        disabled={disabled}
                        IconComponent={() =>
                            isLoading ? (
                                <CircularProgress
                                    size={24}
                                    sx={{
                                        position: 'absolute',
                                        right: '16px',
                                        transform: 'translateY(-50%)',
                                    }}
                                />
                            ) : isDropDownOpen ? (
                                <ArrowDropUpIcon
                                    sx={{
                                        marginRight: '4px',
                                        opacity: 0.5,
                                        cursor: 'pointer',
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        setIsDropDownOpen(false)
                                    }}
                                    id={
                                        id
                                            ? `${id}-generic-dropdown-select-arrow-up`
                                            : `generic-dropdown-select-${name}-arrow-up`
                                    }
                                />
                            ) : (
                                <ArrowDropDownIcon
                                    sx={{
                                        marginRight: '4px',
                                        opacity: 0.5,
                                        cursor: 'pointer',
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        setIsDropDownOpen(true)
                                    }}
                                    id={
                                        id
                                            ? `${id}-generic-dropdown-select-arrow-down`
                                            : `generic-dropdown-select-${name}-arrow-down`
                                    }
                                />
                            )
                        }
                    >
                        {canAdd && (
                            <MenuItem
                                id={`generic-dropdown-menuitem-add-${name}`}
                                sx={{ background: '#fff !important' }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '100%',
                                        cursor: 'pointer',
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        setIsDropDownOpen(false)
                                        onClickAddNew?.()
                                    }}
                                >
                                    <AddRoundedIcon color="primary" />
                                    <Typography
                                        fontWeight="600"
                                        fontSize="14px"
                                        sx={{ marginLeft: '5px' }}
                                    >
                                        {t('addNew')}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        )}

                        {options?.map((option, index) => (
                            <MenuItem
                                id={`generic-dropdown-menuitem-${name}-${index}`}
                                key={option.id}
                                value={option.id}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    '&:hover .edit-icon': {
                                        display: 'block',
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {t(option.name)}
                                </Box>
                                {option?.canEdit && onClickEditRow && (
                                    <IconButton
                                        className="edit-icon"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            setIsDropDownOpen(false)
                                            onClickEditRow(option)
                                        }}
                                        sx={{
                                            display: 'none',
                                            padding: 0,
                                            marginLeft: 'auto',
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
        </Box>
    )
}

export default GenericDropdown
